// src/Config/subscription.types.ts
/**
 * Typed interfaces for subscription-related API responses and data
 * Replaces scattered `any` usage with compile-time type checking
 */

export interface RazorpaySubscription {
  id: string;
  plan_id?: string;
  customer_id?: string;
  status: string;
  current_start?: number;
  current_end?: number;
  ended_at?: number;
  quantity?: number;
  notes?: Record<string, any>;
  [key: string]: any;
}

export interface SubscribeDetailsModel {
  subscription_id?: string;
  razorpay_subscription_id?: string;
  subscriptionId?: string;
  id?: string;
  subscription?: {
    id?: string;
    subscription_id?: string;
    razorpay_subscription_id?: string;
    subscriptionId?: string;
  };
  status?: "created" | "active" | "scheduled_cancel" | "cancelled" | "expired" | "halted";
  expirationDate?: string;
  isScheduledCancel?: boolean;
  [key: string]: any;
}

export interface NewOrderResponse {
  status: boolean;
  message: string;
  data: {
    subscription: RazorpaySubscription;
    order: {
      id: string;
      creatorId: string;
      userId: string;
      subscriptionId: string;
      amount: number;
      status: string;
      [key: string]: any;
    };
  };
}

export interface CheckSubscriptionStatusResponse {
  status?: boolean | string;
  message?: string;
  isActive: boolean;
  creatorId?: string;
  error?: string | { message?: string };
  [key: string]: any;
}

export interface SubscribeHandlerResponse {
  status: boolean;
  message: string;
  data: {
    subscriptionId: string;
    userId: string;
    creatorId: string;
    [key: string]: any;
  };
}

export interface CancelSubscriptionResponse {
  status: boolean;
  message: string;
  data?: {
    subscription_id?: string;
    scheduledCancelAt?: string | null;
    validUntil?: string | null;
    providerCancellationSubmitted?: boolean;
    accessPreservedUntil?: string | null;
  };
  scheduledCancelAt?: string | null;
  validUntil?: string | null;
  providerCancellationSubmitted?: boolean;
  accessPreservedUntil?: string | null;
}

export interface SubscriptionVerificationPayload {
  subscriptionId: string;
  creatorId: string;
}

export interface SubscriptionCancelPayload {
  subscription_id?: string;
  subscriptionId?: string;
  creator_id?: string;
  creatorId?: string;
  subscribeDetails?: SubscribeDetailsModel;
}

/**
 * Local state for the current subscription being viewed/managed
 */
export interface SubscriptionState {
  verifying: boolean;
  cancelling: boolean;
  error: string | null;
  lastAction?: "verify" | "cancel";
}

/**
 * Helper to get subscription ID from flexible backend response
 */
export const getSubscriptionId = (
  details: SubscribeDetailsModel | null | undefined
): string | null => {
  if (!details) return null;

  const normalizeId = (value: unknown): string | null => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed.length) return null;
    return trimmed.startsWith("sub_") ? trimmed : null;
  };

  const directCandidates = [
    details.subscription_id,
    details.razorpay_subscription_id,
    details.subscriptionId,
  ];

  for (const candidate of directCandidates) {
    const normalized = normalizeId(candidate);
    if (normalized) return normalized;
  }

  const nestedCandidates = [
    details.subscription?.subscription_id,
    details.subscription?.razorpay_subscription_id,
    details.subscription?.subscriptionId,
  ];

  for (const candidate of nestedCandidates) {
    const normalized = normalizeId(candidate);
    if (normalized) return normalized;
  }

  const looseIdCandidates = [details.id, details.subscription?.id];
  for (const candidate of looseIdCandidates) {
    const normalized = normalizeId(candidate);
    if (normalized) return normalized;
  }

  return null;
};
